import { Injectable } from '@angular/core';
import { BaseHttpService, PageResponse } from './base-capacitor-http';
import { HttpClient } from '@angular/common/http';
import { AuthResponse, EncryptedData, LoginCredential, UpdateResponse, UserInfo } from './auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';


export class UserDisplay {
  id: number;
  username: string;
  role: string;
  email: string;
  verified: boolean;
  disabled: boolean;
  selected?: boolean = false;
  password?: string;
  confirmPassword?: string;
}
export class UserStats {
  total: number;
  active: number;
  disabled: number;
  verified: number;
}
export class MemberSearchParams {
  page: number;
  size: number;
  search: string;
  verified: boolean;
  disabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MemberManagementService extends BaseHttpService {

  private readonly baseUrl: string;
  private urlEndPoint: string;

  constructor(client: HttpClient, snackBar: MatSnackBar) {
    super(client, snackBar);
    this.baseUrl = environment.server.mainURL + 'members/';
  }

  async checkUsernameExists(username: string) {
    this.urlEndPoint = this.baseUrl + 'admin/check-username';
    const payload = {
      username: this.hashValue(username)
    }
    let body = {
      payload: this.encrypt<any>(payload, this.KEY)
    };
    return this.post<boolean>(this.urlEndPoint, body);
  }

  async checkEmailExists(email: string) {
    this.urlEndPoint = this.baseUrl + 'admin/check-email';
    const payload = {
      email: this.hashValue(email)
    }

    let body = {
      payload: this.encrypt<any>(payload, this.KEY)
    };
    return this.post<boolean>(this.urlEndPoint, body);
  }

  async getUserStats() {
    this.urlEndPoint = this.baseUrl + 'admin/user-stats';
    return this.get<UserStats>(this.urlEndPoint);
  }

  async getAllMemberData(params: MemberSearchParams) {
    this.urlEndPoint = this.baseUrl + 'admin/user';
    const encryptedData = await this.get<EncryptedData>(this.urlEndPoint, params);
    return this.decryptResponse<PageResponse<UserDisplay>>(encryptedData.payload, this.KEY, encryptedData.iv);
  }

  async updateMember(user: UserDisplay) {
    this.urlEndPoint = this.baseUrl + 'admin/user';
    let body = {
      payload: this.encrypt<UserDisplay>(user, this.KEY)
    }
    const encryptedData = await this.put<EncryptedData>(this.urlEndPoint, body);
    return this.decryptResponse<UserDisplay>(encryptedData.payload, this.KEY, encryptedData.iv);
  }

  async deleteMember(userId: number) {
    this.urlEndPoint = this.baseUrl + 'admin/user/' + userId;
    return this.delete<boolean>(this.urlEndPoint);
  }

  async bulkUpdate(update: string, users: UserDisplay[]) {
    this.urlEndPoint = this.baseUrl + 'admin/bulk-update';
    let body = {
      payload: this.encrypt<UserDisplay[]>(users, this.KEY)
    }
    const encryptedData = await this.put<EncryptedData>(this.urlEndPoint, body, { update: update });
    return this.decryptResponse<UserDisplay[]>(encryptedData.payload, this.KEY, encryptedData.iv);
  }


  async registerMember(user: UserDisplay) {
    this.urlEndPoint = this.baseUrl + 'admin/register';
    user.password = this.hashValue(user.password);
    user.confirmPassword = this.hashValue(user.confirmPassword);

    let body = {
      payload: this.encrypt<UserDisplay>(user, this.KEY),
      url: environment.server.domain
    };
    const encryptedData = await this.post<EncryptedData>(this.urlEndPoint, body);
    return this.decryptResponse<UserDisplay>(encryptedData.payload, this.KEY, encryptedData.iv);
  }

  async checkAccountStatus(): Promise<AuthResponse> {
    this.urlEndPoint = this.baseUrl + 'account-status';
    return this.get<AuthResponse>(this.urlEndPoint);
  }

  async reAuthenticate(credential: LoginCredential) {
    let key = environment.server.key;
    this.urlEndPoint = this.baseUrl + 're-authenticate';

    credential.password = this.hashValue(credential.password);
    credential.email = this.hashValue(credential.email);

    let body = {
      payload: this.encrypt<LoginCredential>(credential, key)
    }

    return this.post<AuthResponse>(this.urlEndPoint, body);
  }

  async loadUserInfo() {
    this.urlEndPoint = this.baseUrl + 'info';
    const encryptedData = await this.get<EncryptedData>(this.urlEndPoint);
    return this.decryptResponse<UserInfo>(encryptedData.payload, this.KEY, encryptedData.iv);
  }

  async updatePassword(password: string, newPassword: string) {
    this.urlEndPoint = this.baseUrl + 'update-password';
    let body = {
      password: this.hashValue(password),
      newPassword: this.hashValue(newPassword)
    }
    return this.post<boolean>(this.urlEndPoint, body);
  }

  async updateUsername(username: string): Promise<UpdateResponse> {
    this.urlEndPoint = this.baseUrl + 'update-password';
    let body = {
      username: username
    }

    return this.post<UpdateResponse>(this.urlEndPoint, body);
  }

  async getUserHashedEmail() {
    this.urlEndPoint = this.baseUrl + 'masked-email';
    return this.get<{ email: string }>(this.urlEndPoint);
  }
}
